import { useMemo } from 'react'
import { ChevronLeftIcon, ClearIcon, ToolsIcon } from '@weblite/icons'
import { alpha, ButtonBase, IconButton, styled, useTheme, darken } from '@mui/material'
import { WAPP_DEV_ENV_WAPP_ID } from 'weblite-web-wapprunner'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import {
  MULTIPLE_CHOICE_EXAM_CONFIG,
  POLL_CONFIG,
  UNIT_TEST_CONFIG,
  WAPP_DEV_ENV_CONFIG,
} from '$common/wapp-bar-theme'
import {
  ChatListToolboxListItem,
  ChatListToolboxListItemVariant,
} from '$comp/chat-list-tool-box/components/list-item/list-item'
import { otherWappsConfig } from '$comp/chat-list-tool-box/components/other-wapps/other-wapps'
import { showChatListToolboxOtherWappsModal } from '$comp/modals/chat-list-toolbox/chat-list-toolbox-other-wapps-modal.api'
import { WappPanel } from '$logic/wapp-panels'
import {
  openMultipleChoiceExamCreator,
  openPollCreator,
  openUnitTestCreator,
} from '$logic/wapps/business'
import hostConf from '$config/frontend'
import { t } from '$i18n'

interface Props {
  onClose?: () => void
}

const CLOSE_BUTTON_WIDTH = 52

const otherWappsNames = otherWappsConfig.reduce(
  (prev, config, index, array) => {
    const isLast = index === array.length - 1

    // TODO[toolbox]: Use i18n
    const delimiter = !prev ? '' : isLast ? ' و ' : '، '
    return `${prev}${delimiter}${config.name}`
  },
  '',
)

export function ChatListToolboxHome({ onClose }: Props) {
  const theme = useTheme()

  const wappsConfig = useMemo(
    () => [
      {
        ...MULTIPLE_CHOICE_EXAM_CONFIG,
        borderColor: theme.palette.error500,
        backgroundColor: alpha(theme.palette.error500, 0.1),
        hoverColor: alpha(theme.palette.error500, 0.25),
        variant: 'fullWidth',
        description: t('chatListToolbox.wappDescription.generalExam'),
        onClick: openMultipleChoiceExamCreator,
        logoGradient: 'linear-gradient(180deg, #FF7676 0%, #D10000 100%)',
        key: 'MULTIPLE_CHOICE_EXAM',
      },
      {
        ...UNIT_TEST_CONFIG,
        backgroundColor: alpha(theme.palette.primary500, 0.1),
        borderColor: theme.palette.primary500,
        hoverColor: alpha(theme.palette.primary500, 0.25),
        variant: 'halfWidth',
        description: t('chatListToolbox.wappDescription.unitTest'),
        onClick: openUnitTestCreator,
        logoGradient: 'linear-gradient(180deg, #00BAFF 0%, #093DEB 100%)',
        key: 'UNIT_TEST',
      },
      {
        ...POLL_CONFIG,
        backgroundColor: alpha(theme.palette.yellow500, 0.1),
        borderColor: theme.palette.yellow500,
        hoverColor: alpha(theme.palette.yellow500, 0.25),
        variant: 'halfWidth',
        description: t('chatListToolbox.wappDescription.poll'),
        onClick: openPollCreator,
        logoGradient: 'linear-gradient(180deg, #FFF35F 0%, #FFB100 100%)',
        key: 'POLL',
      },
      hostConf.HAS_WAPP_DEV_ENV && {
        ...WAPP_DEV_ENV_CONFIG,
        backgroundColor: '#E8F0FF',
        borderColor: '#3B80F0',
        variant: 'fullWidth',
        description: t('chatListToolbox.wappDescription.wappDevEnv'),
        onClick: () => WappPanel.openWapp({ wappId: WAPP_DEV_ENV_WAPP_ID }),
        logoGradient: 'linear-gradient(0deg, #284FE7 4.55%, #53BEFB 95.45%)',
        hoverColor: `${darken('#E8F0FF', 0.1)}`,
      },
    ],
    [theme],
  )

  return (
    <>
      <Header>
        <HeaderBackground />
        <CloseButton onClick={onClose}>
          <ClearIcon sx={{ width: '24px', height: '24px' }} />
        </CloseButton>
      </Header>
      <WappsGrid>
        {wappsConfig.map(wappConfig => (
          <ChatListToolboxListItem
            key={wappConfig.key}
            icon={wappConfig.icon}
            title={wappConfig.name}
            description={wappConfig.description}
            borderColor={wappConfig.borderColor}
            backgroundColor={wappConfig.backgroundColor}
            onClick={async () => {
              await closeCurrentModal()
              wappConfig.onClick()
            }}
            variant={wappConfig.variant as ChatListToolboxListItemVariant}
            gradient={wappConfig.logoGradient}
            hoverColor={wappConfig.hoverColor}
          />
        ))}

        <ChatListToolboxListItem
          icon={<ToolsIcon sx={{ transform: 'scale(0.625)' }} />}
          title={t('chatListToolbox.otherWapps')}
          description={otherWappsNames}
          borderColor={theme.palette.neutral400}
          backgroundColor={alpha(theme.palette.neutral400, 0.1)}
          hoverColor={alpha(theme.palette.neutral400, 0.25)}
          onClick={async () => {
            await closeCurrentModal()
            showChatListToolboxOtherWappsModal()
          }}
          variant="fullWidth"
          gradient={theme.palette.neutral600}
          endIconButton={
            <IconButton>
              <ChevronLeftIcon sx={{ color: 'neutral600' }} />
            </IconButton>
          }
        />
      </WappsGrid>
    </>
  )
}

const Header = styled('div')`
  margin-top: ${CLOSE_BUTTON_WIDTH / 2}px;
  width: 100%;
  height: 40px;
`

const HeaderBackground = styled('div')`
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 16px 16px 0 0;
  position: relative;
  &:after {
    content: '';
    position: absolute;
    width: 62px;
    height: 62px;
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    box-shadow: 0 0 0 2000px ${({ theme }) => theme.palette.neutral000};
  }
`

const CloseButton = styled(ButtonBase)`
  width: ${CLOSE_BUTTON_WIDTH}px;
  height: ${CLOSE_BUTTON_WIDTH}px;
  background-color: ${({ theme }) => theme.palette.neutral000};
  &:hover {
    background-color: ${({ theme }) => theme.palette.neutral100};
  }
  color: ${({ theme }) => theme.palette.neutral700};
  position: absolute;
  left: 50%;
  top: 0;
  transform: translateX(-50%);
  border-radius: 50%;
`

const WappsGrid = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 6px;
  background-color: ${({ theme }) => theme.palette.neutral000};
  padding: 8px 12px 12px;
`
